// =============================================
// AuthContext.jsx - 로그인 상태 전역 관리
// =============================================
// React의 Context API를 사용해서 로그인 정보를
// 앱 전체에서 공유해.
//
// Context란? 컴포넌트 트리 어디서든 데이터를
// props 없이 바로 꺼내 쓸 수 있게 해주는 기능이야.
//
// ★ 이 Context가 제공하는 값들:
//   - user        : 로그인한 사용자 정보 (이름, 이메일, 로그인방식)
//   - token       : JWT 토큰 문자열
//   - isLoggedIn  : 로그인 여부 (true/false)
//   - login(token): 로그인 처리 함수 (토큰 저장 + 상태 업데이트)
//   - logout()    : 로그아웃 처리 함수 (토큰 삭제 + 상태 초기화)
//
// ★ axios 인터셉터:
//   로그인 후 모든 axios 요청에 자동으로
//   "Authorization: Bearer <토큰>" 헤더를 붙여줘.
//   → 매 API 요청마다 헤더를 직접 추가할 필요가 없어!
// =============================================

import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// =============================================
// Context 생성
// =============================================
// createContext(): 전역 상태를 담을 그릇(Context)을 만들어.
// null은 Provider 없이 useAuth()를 호출했을 때의 기본값이야.
const AuthContext = createContext(null);


// =============================================
// JWT 디코드 헬퍼
// =============================================
// JWT는 3부분으로 구성돼: header.payload.signature
// payload 부분만 base64 디코딩하면 사용자 정보를 꺼낼 수 있어.
// (서명 검증은 백엔드가 담당해 — 프론트는 내용만 읽으면 돼)
function decodeJWT(token) {
  try {
    const payload = token.split('.')[1];       // 두 번째 .으로 구분된 부분
    const decoded = atob(payload);             // base64 디코딩
    return JSON.parse(decoded);                // JSON → 객체
  } catch {
    return null;  // 형식이 잘못된 토큰
  }
}


// =============================================
// AuthProvider 컴포넌트
// =============================================
// App.jsx에서 전체 앱을 감싸는 컴포넌트야.
// <AuthProvider> 안의 모든 컴포넌트에서 useAuth() 훅을 쓸 수 있어.
export function AuthProvider({ children }) {

  // ── 상태 정의 ──
  // token  : localStorage에서 읽어온 JWT 토큰 (없으면 null)
  // user   : JWT에서 디코딩한 사용자 정보 (없으면 null)
  const [token, setToken] = useState(() => {
    // ★ useState 초기화 함수: 앱 시작 시 localStorage에서 토큰 복원
    // → 페이지를 새로고침해도 로그인 상태가 유지돼!
    return localStorage.getItem('auth_token') || null;
  });

  const [user, setUser] = useState(() => {
    // 앱 시작 시 localStorage의 토큰에서 사용자 정보 복원
    const savedToken = localStorage.getItem('auth_token');
    if (!savedToken) return null;
    const payload = decodeJWT(savedToken);
    // JWT 만료 확인 (exp는 Unix timestamp, 초 단위)
    if (payload && payload.exp && payload.exp * 1000 < Date.now()) {
      // 만료된 토큰 → 자동 로그아웃
      localStorage.removeItem('auth_token');
      return null;
    }
    return payload ? {
      name:     payload.name,
      email:    payload.email,
      provider: payload.provider,
      id:       payload.sub,
    } : null;
  });


  // =============================================
  // ★ axios 인터셉터 설정
  // =============================================
  // useEffect: 컴포넌트가 마운트될 때 한 번 실행돼.
  // axios.interceptors.request.use(): 모든 요청 직전에 실행되는 함수 등록.
  // → 토큰이 있으면 Authorization 헤더를 자동으로 추가해줘.
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use((config) => {
      // ① localStorage에서 최신 토큰 읽기 (상태와 동기화)
      const currentToken = localStorage.getItem('auth_token');
      if (currentToken) {
        // ② Authorization 헤더 추가
        // 형식: "Bearer eyJhbGciOiJIUzI1NiIsInR5..."
        config.headers['Authorization'] = `Bearer ${currentToken}`;
      }
      return config;  // ③ 수정된 config를 반환 (요청 계속 진행)
    });

    // 컴포넌트 언마운트 시 인터셉터 제거 (메모리 누수 방지)
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, []);  // 빈 배열 → 최초 마운트 시 한 번만 실행


  // =============================================
  // 로그인 함수
  // =============================================
  // 백엔드에서 받은 JWT 토큰을 저장하고 상태를 업데이트해.
  // → LoginPage에서 구글/텔레그램 인증 성공 후 호출해.
  const login = (newToken) => {
    // ① localStorage에 저장 (새로고침 후에도 유지)
    localStorage.setItem('auth_token', newToken);

    // ② 상태 업데이트
    setToken(newToken);

    // ③ JWT에서 사용자 정보 추출
    const payload = decodeJWT(newToken);
    if (payload) {
      setUser({
        name:     payload.name,
        email:    payload.email,
        provider: payload.provider,
        id:       payload.sub,
      });
    }
  };


  // =============================================
  // 로그아웃 함수
  // =============================================
  const logout = () => {
    // ① localStorage에서 토큰 삭제
    localStorage.removeItem('auth_token');

    // ② 상태 초기화
    setToken(null);
    setUser(null);
  };


  // =============================================
  // Context 값 제공
  // =============================================
  // value 객체의 모든 값이 useAuth()를 통해 하위 컴포넌트에 전달돼.
  return (
    <AuthContext.Provider value={{
      token,
      user,
      isLoggedIn: !!token && !!user,  // 토큰과 사용자 정보가 모두 있으면 true
      login,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}


// =============================================
// useAuth 훅 (편의 함수)
// =============================================
// 컴포넌트에서 useAuth()를 호출하면 AuthContext 값을 꺼낼 수 있어.
// 예시:
//   const { user, isLoggedIn, login, logout } = useAuth();
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth()는 <AuthProvider> 안에서만 사용할 수 있어!');
  }
  return ctx;
}

export default AuthContext;
